import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErr } from "@/lib/format";
import {
  MegaphoneIcon, FilePdfIcon, EyeIcon, TrashIcon, PlusIcon, XIcon, ImageIcon, DownloadSimpleIcon, PaperclipIcon,
  ArrowSquareOutIcon, WarningCircleIcon,
  XCircleIcon
} from "@phosphor-icons/react";

// Fecha de publicación tal como se registró (AAAA-MM-DD → DD/MM/AAAA), sin conversión de zona horaria
const fFecha = (f) => { const [a, m, d] = String(f || "").slice(0, 10).split("-"); return d ? `${d}/${m}/${a}` : "—"; };
const fTam = (n) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const esImagen = (tipo) => String(tipo || "").startsWith("image/");

// Formatos y tamaños permitidos (el servidor valida lo mismo, también el contenido real del archivo)
const EXT_OK = ["pdf", "jpg", "jpeg", "png"];
const MAX_ARCHIVO = 10 * 1024 * 1024;
const MAX_TOTAL = 40 * 1024 * 1024;
const MAX_ADJUNTOS = 10;

/** Descarga un archivo protegido (con la sesión del usuario) y lo guarda con su nombre. */
async function bajar(url, nombre, tipo) {
  const { data } = await api.get(url, { responseType: "blob" });
  const href = URL.createObjectURL(new Blob([data], { type: tipo || data.type }));
  const a = document.createElement("a");
  a.href = href;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 3000);
}

/** URL local (blob) de un adjunto, para ver la imagen o abrir el PDF dentro del sistema. */
function useBlobUrl(url, activo = true) {
  const [src, setSrc] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    if (!activo || !url) return undefined;
    let vivo = true;
    let creado = null;
    api.get(url, { responseType: "blob" })
      .then(({ data }) => { if (vivo) { creado = URL.createObjectURL(data); setSrc(creado); } })
      .catch(() => vivo && setError(true));
    return () => { vivo = false; if (creado) URL.revokeObjectURL(creado); };
  }, [url, activo]);
  return { src, error };
}

const adjUrl = (c, a, inline = false) => `/comunicaciones/${c.id}/adjuntos/${a.id}${inline ? "?inline=true" : ""}`;

/**
 * Comunicaciones: avisos y comunicados oficiales en el Dashboard de todos los usuarios.
 * Todos consultan, ven imágenes, abren los PDF y descargan; solo el Administrador General carga, publica y elimina
 * (el servidor también lo exige).
 */
export default function Comunicaciones() {
  const [items, setItems] = useState(null);
  const [admin, setAdmin] = useState(false);
  const [ver, setVer] = useState(null);          // comunicado completo abierto
  const [visor, setVisor] = useState(null);      // {c, a} adjunto abierto
  const [nuevo, setNuevo] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const { data } = await api.get("/comunicaciones");
      setItems(data.items || []);
      setAdmin(Boolean(data.puede_administrar));
    } catch { setItems([]); }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const abrir = async (c) => {
    try { setVer((await api.get(`/comunicaciones/${c.id}`)).data); } catch (e) { toast.error(apiErr(e)); }
  };
  const eliminar = async (c) => {
    if (!window.confirm(`¿Eliminar el comunicado «${c.titulo}» y sus archivos? Dejará de estar disponible para todos los usuarios.`)) return;
    try { await api.delete(`/comunicaciones/${c.id}`); toast.success("Comunicado eliminado"); cargar(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const eliminarAdjunto = async (c, a) => {
    if (!window.confirm(`¿Eliminar el archivo «${a.nombre}» de esta publicación?`)) return;
    try { await api.delete(adjUrl(c, a)); toast.success("Archivo eliminado"); cargar(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (items === null) return null;
  return (
    <section className="bg-card border border-border/60 rounded-lg" data-testid="comunicaciones">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-5 py-4 border-b border-border/60">
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <MegaphoneIcon size={18} className="text-agro-green" />
          <h2 className="font-heading text-base font-semibold">Comunicaciones</h2>
          <span className="text-xs text-text-muted">Avisos y comunicados oficiales</span>
        </div>
        {admin && (
          <button type="button" onClick={() => setNuevo(true)} data-testid="comunicado-nuevo"
            className="text-xs font-semibold rounded-md px-3 py-1.5 flex items-center gap-1.5 bg-agro-green/10 text-agro-green border border-agro-green/30 hover:bg-agro-green/20">
            <PlusIcon size={13} /> Publicar comunicado
          </button>
        )}
      </div>
      {items.length === 0 ? (
        <div className="px-5 py-6 text-sm text-text-muted">No hay comunicaciones publicadas.</div>
      ) : (
        <ul className="divide-y divide-border/50">
          {items.map((c) => (
            <li key={c.id} className="px-5 py-4 space-y-3" data-testid={`comunicado-${c.id}`}>
              <div className="flex flex-col md:flex-row md:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <h3 className="font-semibold text-text-primary" data-testid="comunicado-titulo">{c.titulo}</h3>
                    <span className="text-xs text-text-muted" data-testid="comunicado-fecha">Publicado el {fFecha(c.fecha_publicacion)}</span>
                  </div>
                  <p className="text-sm text-text-secondary mt-1" data-testid="comunicado-descripcion">{c.descripcion}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {c.tiene_contenido && (
                    <>
                      <button type="button" onClick={() => abrir(c)} data-testid="comunicado-ver"
                        className="text-xs font-medium rounded-md px-3 py-1.5 flex items-center gap-1.5 border border-border hover:border-agro-green/50 hover:text-agro-green">
                        <EyeIcon size={14} /> Ver comunicado
                      </button>
                      <BotonPdf c={c} />
                    </>
                  )}
                  {admin && (
                    <button type="button" onClick={() => eliminar(c)} data-testid="comunicado-eliminar" title="Eliminar comunicado" aria-label="Eliminar comunicado"
                      className="p-1.5 rounded-md text-text-muted hover:text-red-400 border border-transparent hover:border-red-500/40">
                      <TrashIcon size={15} />
                    </button>
                  )}
                </div>
              </div>
              {c.adjuntos?.length > 0 && (
                <div className="flex flex-wrap gap-3" data-testid="comunicado-adjuntos">
                  {c.adjuntos.map((a) => (
                    <Adjunto key={a.id} c={c} a={a} admin={admin} onVer={() => setVisor({ c, a })} onEliminar={() => eliminarAdjunto(c, a)} />
                  ))}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {ver && <VerComunicado c={ver} onClose={() => setVer(null)} onVerAdjunto={(a) => setVisor({ c: ver, a })} />}
      {visor && <Visor c={visor.c} a={visor.a} onClose={() => setVisor(null)} />}
      {nuevo && <NuevoComunicado onClose={() => setNuevo(false)} onSaved={() => { setNuevo(false); cargar(); }} />}
    </section>
  );
}

function BotonPdf({ c }) {
  const [bajando, setBajando] = useState(false);
  const descargar = async () => {
    setBajando(true);
    try { await bajar(`/comunicaciones/${c.id}/pdf`, c.archivo_nombre || "Comunicado.pdf", "application/pdf"); }
    catch { toast.error("No se pudo descargar el PDF"); }
    finally { setBajando(false); }
  };
  return (
    <button type="button" onClick={descargar} disabled={bajando} data-testid="comunicado-pdf"
      className="text-xs font-semibold rounded-md px-3 py-1.5 flex items-center gap-1.5 bg-agro-green text-black hover:bg-agro-green-hover disabled:opacity-60">
      <FilePdfIcon size={14} /> {bajando ? "Descargando…" : "Descargar PDF"}
    </button>
  );
}

/** Tarjeta de un adjunto: miniatura (imagen) o ícono (PDF), ver / abrir y descargar en su formato original. */
function Adjunto({ c, a, admin, onVer, onEliminar }) {
  const img = esImagen(a.tipo);
  const { src } = useBlobUrl(adjUrl(c, a, true), img);
  const descargar = async () => {
    try { await bajar(adjUrl(c, a), a.nombre, a.tipo); } catch { toast.error(`No se pudo descargar «${a.nombre}»`); }
  };
  return (
    <div className="w-56 border border-border rounded-md overflow-hidden bg-app" data-testid={`adjunto-${a.nombre}`}>
      <button type="button" onClick={onVer} className="block w-full h-28 bg-black/20 hover:opacity-90" title={img ? "Ver imagen" : "Abrir PDF"}
        data-testid="adjunto-ver">
        {img
          ? (src ? <img src={src} alt={a.nombre} className="w-full h-full object-cover" data-testid="adjunto-miniatura" />
            : <span className="flex items-center justify-center h-full text-text-muted"><ImageIcon size={28} /></span>)
          : <span className="flex flex-col items-center justify-center h-full text-red-300"><FilePdfIcon size={34} /><span className="text-[10px] mt-1 text-text-muted">Abrir PDF</span></span>}
      </button>
      <div className="px-2 py-1.5 flex items-center gap-1.5">
        <div className="flex-1 min-w-0">
          <div className="text-xs truncate" title={a.nombre}>{a.nombre}</div>
          <div className="text-[10px] text-text-muted">{img ? "Imagen" : "PDF"} · {fTam(a.tamano || 0)}</div>
        </div>
        <button type="button" onClick={descargar} title="Descargar archivo original" aria-label="Descargar archivo original" data-testid="adjunto-descargar"
          className="p-1 text-text-muted hover:text-agro-green"><DownloadSimpleIcon size={16} /></button>
        {admin && (
          <button type="button" onClick={onEliminar} title="Eliminar archivo" aria-label="Eliminar archivo" data-testid="adjunto-eliminar"
            className="p-1 text-text-muted hover:text-red-400"><TrashIcon size={14} /></button>
        )}
      </div>
    </div>
  );
}

/** Visor dentro del sistema: la imagen a tamaño completo o el PDF incrustado (con abrir en pestaña y descargar). */
function Visor({ c, a, onClose }) {
  const { src, error } = useBlobUrl(adjUrl(c, a, true));
  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-lg w-full max-w-5xl h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="adjunto-visor">
        <div className="px-4 py-3 border-b border-border flex items-center gap-3">
          <PaperclipIcon size={16} className="text-agro-green" />
          <div className="flex-1 min-w-0 text-sm font-medium truncate">{a.nombre}</div>
          {src && (
            <a href={src} target="_blank" rel="noopener noreferrer" className="text-xs flex items-center gap-1 text-text-secondary hover:text-agro-green">
              <ArrowSquareOutIcon size={14} /> Abrir en pestaña nueva
            </a>
          )}
          <button type="button" onClick={() => bajar(adjUrl(c, a), a.nombre, a.tipo).catch(() => toast.error("No se pudo descargar"))}
            className="text-xs font-semibold rounded-md px-3 py-1.5 flex items-center gap-1.5 bg-agro-green text-black hover:bg-agro-green-hover">
            <DownloadSimpleIcon size={14} /> Descargar
          </button>
          <button aria-label="Cerrar" type="button" onClick={onClose} className="text-text-muted hover:text-text-primary"><XIcon size={20} /></button>
        </div>
        <div className="flex-1 min-h-0 flex items-center justify-center bg-black/30">
          {error ? <div className="text-sm text-red-300 flex items-center gap-2"><WarningCircleIcon size={18} /> No se pudo abrir el archivo.</div>
            : !src ? <div className="text-sm text-text-muted">Cargando…</div>
              : esImagen(a.tipo) ? <img src={src} alt={a.nombre} className="max-w-full max-h-full object-contain" data-testid="visor-imagen" />
                : <iframe src={src} title={a.nombre} className="w-full h-full bg-white" data-testid="visor-pdf" />}
        </div>
      </div>
    </div>
  );
}

/** Texto con **negritas** y párrafos separados por línea en blanco (igual que en el PDF). */
function Texto({ contenido }) {
  const parrafos = String(contenido || "").replace(/\r\n/g, "\n").split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
  return parrafos.map((p, i) => (
    <p key={i} className="mb-3 leading-relaxed whitespace-pre-line">
      {p.split(/(\*\*[^*]+\*\*)/g).map((t, k) => (t.startsWith("**") && t.endsWith("**")
        ? <strong key={k} className="font-semibold text-text-primary">{t.slice(2, -2)}</strong> : <span key={k}>{t}</span>))}
    </p>
  ));
}

function VerComunicado({ c, onClose, onVerAdjunto }) {
  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}
        data-testid="comunicado-detalle">
        <div className="p-5 border-b border-border flex items-start justify-between gap-3 sticky top-0 bg-card">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-agro-yellow">Comunicado oficial</div>
            <h3 className="font-heading text-lg font-semibold">{c.titulo}</h3>
            <div className="text-xs text-text-muted">{fFecha(c.fecha_publicacion)}</div>
          </div>
          <div className="flex items-center gap-2">
            {c.tiene_contenido && <BotonPdf c={c} />}
            <button aria-label="Cerrar" type="button" onClick={onClose} className="text-text-muted hover:text-text-primary"><XIcon size={20} /></button>
          </div>
        </div>
        <div className="p-6 text-sm text-text-secondary" data-testid="comunicado-contenido">
          <Texto contenido={c.contenido} />
          {c.adjuntos?.length > 0 && (
            <div className="mt-4 pt-4 border-t border-border">
              <div className="text-[10px] uppercase tracking-wider text-text-muted mb-2">Archivos adjuntos</div>
              <div className="flex flex-wrap gap-2">
                {c.adjuntos.map((a) => (
                  <button key={a.id} type="button" onClick={() => onVerAdjunto(a)}
                    className="text-xs border border-border rounded-md px-2.5 py-1.5 flex items-center gap-1.5 hover:border-agro-green/50 hover:text-agro-green">
                    {esImagen(a.tipo) ? <ImageIcon size={14} /> : <FilePdfIcon size={14} />} {a.nombre}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function NuevoComunicado({ onClose, onSaved }) {
  const hoy = new Date().toLocaleDateString("en-CA");
  const [f, setF] = useState({ titulo: "", fecha_publicacion: hoy, descripcion: "", contenido: "", archivo_nombre: "" });
  const [archivos, setArchivos] = useState([]);   // [{id, nombre, tipo, tamano, dataUrl}]
  const [rechazados, setRechazados] = useState([]);  // [{nombre, motivo}]
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const leer = (file) => new Promise((ok, ko) => {
    const r = new FileReader();
    r.onload = () => ok(r.result);
    r.onerror = () => ko(new Error("lectura"));
    r.readAsDataURL(file);
  });
  const agregar = async (e) => {
    const lista = Array.from(e.target.files || []);
    e.target.value = "";
    const malos = [];
    const nuevos = [];
    let total = archivos.reduce((t, x) => t + x.tamano, 0);
    for (const file of lista) {
      const ext = (file.name.split(".").pop() || "").toLowerCase();
      if (!file.name.includes(".") || !EXT_OK.includes(ext)) { malos.push({ nombre: file.name, motivo: "formato no permitido (solo PDF, JPG, JPEG o PNG)" }); continue; }
      if (file.size === 0) { malos.push({ nombre: file.name, motivo: "el archivo está vacío" }); continue; }
      if (file.size > MAX_ARCHIVO) { malos.push({ nombre: file.name, motivo: `pesa ${fTam(file.size)}; el máximo es 10 MB` }); continue; }
      if (archivos.length + nuevos.length >= MAX_ADJUNTOS) { malos.push({ nombre: file.name, motivo: `máximo ${MAX_ADJUNTOS} archivos por publicación` }); continue; }
      if (total + file.size > MAX_TOTAL) { malos.push({ nombre: file.name, motivo: "los archivos juntos superarían 40 MB" }); continue; }
      try {
        const dataUrl = await leer(file);
        nuevos.push({ id: `${file.name}-${file.size}-${Math.random()}`, nombre: file.name, tipo: ext === "pdf" ? "application/pdf" : `image/${ext === "jpg" ? "jpeg" : ext}`, tamano: file.size, dataUrl });
        total += file.size;
      } catch { malos.push({ nombre: file.name, motivo: "no se pudo leer el archivo" }); }
    }
    setArchivos((prev) => [...prev, ...nuevos]);
    setRechazados(malos);
    if (malos.length) toast.error(`${malos.length} archivo(s) no se pueden cargar; revisa el detalle.`);
  };
  const quitar = (id) => setArchivos((prev) => prev.filter((x) => x.id !== id));

  const guardar = async (e) => {
    e.preventDefault();
    if (f.titulo.trim().length < 3 || f.descripcion.trim().length < 3) { toast.error("Completa el título y la descripción breve"); return; }
    if (!archivos.length && f.contenido.trim().length < 3) { toast.error("Adjunta al menos un archivo o escribe el texto del comunicado"); return; }
    setSaving(true);
    try {
      await api.post("/comunicaciones", {
        ...f, archivo_nombre: f.archivo_nombre || null,
        adjuntos: archivos.map((x) => ({ nombre: x.nombre, data: x.dataUrl })),
      });
      toast.success("Comunicado publicado");
      onSaved();
    } catch (err) { toast.error(apiErr(err)); }
    finally { setSaving(false); }
  };
  const input = "w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green";
  const Lbl = ({ children }) => <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">{children}</label>;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <form onSubmit={guardar} className="bg-card border border-border rounded-lg w-full max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="comunicado-form">
        <div className="p-5 border-b border-border flex items-center justify-between sticky top-0 bg-card z-10">
          <h3 className="font-heading text-lg font-semibold">Publicar comunicado</h3>
          <button aria-label="Cerrar" type="button" onClick={onClose} className="text-text-muted hover:text-text-primary"><XIcon size={20} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="md:col-span-3"><Lbl>Título *</Lbl>
              <input value={f.titulo} onChange={set("titulo")} className={input} data-testid="com-titulo" maxLength={200} /></div>
            <div><Lbl>Fecha de publicación *</Lbl>
              <input type="date" value={f.fecha_publicacion} onChange={set("fecha_publicacion")} className={input} data-testid="com-fecha" /></div>
          </div>
          <div><Lbl>Descripción breve *</Lbl>
            <textarea rows={2} value={f.descripcion} onChange={set("descripcion")} className={input} data-testid="com-descripcion" maxLength={600} /></div>

          <div>
            <Lbl>Archivos adjuntos (PDF, JPG, JPEG o PNG · máx. 10 MB c/u)</Lbl>
            <label className="flex items-center gap-2 px-3 py-3 border border-dashed border-border rounded-md text-sm text-text-secondary hover:border-agro-green/40 cursor-pointer">
              <PaperclipIcon size={16} /> Seleccionar uno o varios archivos
              <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" onChange={agregar} className="hidden" data-testid="com-archivos" />
            </label>
            {archivos.length > 0 && (
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="com-seleccionados">
                {archivos.map((x) => (
                  <div key={x.id} className="flex items-center gap-2 border border-border rounded-md p-2 bg-app" data-testid={`com-sel-${x.nombre}`}>
                    {esImagen(x.tipo) ? <img src={x.dataUrl} alt={x.nombre} className="w-12 h-12 object-cover rounded" />
                      : <span className="w-12 h-12 flex items-center justify-center rounded bg-card text-red-300"><FilePdfIcon size={24} /></span>}
                    <div className="flex-1 min-w-0">
                      <div className="text-xs truncate" title={x.nombre}>{x.nombre}</div>
                      <div className="text-[10px] text-text-muted">{fTam(x.tamano)}</div>
                    </div>
                    <button type="button" onClick={() => quitar(x.id)} title="Retirar" aria-label="Retirar" data-testid="com-quitar"
                      className="p-1 text-text-muted hover:text-red-400"><TrashIcon size={15} /></button>
                  </div>
                ))}
              </div>
            )}
            {rechazados.length > 0 && (
              <div className="mt-2 text-xs text-red-300 border border-red-500/40 bg-red-500/10 rounded-md p-2 space-y-0.5" data-testid="com-rechazados">
                {rechazados.map((x, i) => <div key={i}><XCircleIcon size={12} className="inline align-[-2px]" aria-hidden="true" /> «{x.nombre}»: {x.motivo}</div>)}
              </div>
            )}
          </div>

          <details className="border border-border rounded-md p-3" open={false}>
            <summary className="text-xs text-text-secondary cursor-pointer select-none">Texto del comunicado (opcional) — genera el PDF con la plantilla institucional</summary>
            <div className="mt-3 space-y-3">
              <textarea rows={10} value={f.contenido} onChange={set("contenido")} className={`${input} font-mono text-xs`} data-testid="com-contenido" />
              <div className="text-[11px] text-text-muted">Separa los párrafos con una línea en blanco. Escribe <b>**texto**</b> para negrita.</div>
              <div><Lbl>Nombre del PDF institucional (opcional)</Lbl>
                <input value={f.archivo_nombre} onChange={set("archivo_nombre")} placeholder="Comunicado_….pdf" className={input} data-testid="com-archivo" /></div>
            </div>
          </details>

          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="px-4 py-3 rounded-md border border-border text-sm hover:border-text-muted">Cancelar</button>
            <button type="submit" disabled={saving} data-testid="com-publicar"
              className="flex-1 bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded-md py-3 disabled:opacity-50">
              {saving ? "Publicando…" : `Publicar para todos los usuarios${archivos.length ? ` · ${archivos.length} archivo(s)` : ""}`}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
